import React, { useEffect, useState } from "react";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, CreditCard, ShieldCheck, CheckCircle2, Home, Building2 } from "lucide-react";

/**
 * Subscribe page - Shows OMPay-hosted checkout plans (standard 5 OMR = 1
 * property, premium 9 OMR = 3 properties). When the user is redirected here
 * because they hit their property limit (`?reason=extra`), we hide plans
 * whose `max_properties` is not greater than their current property count —
 * so no misleading "5 OMR" for someone who needs the premium tier.
 */
export default function Subscribe({ embedded = false }) {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState({});
  const [ownedCount, setOwnedCount] = useState(null);

  // Parse `?reason=extra` from URL to know if we're upgrading for more properties
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
  const reason = params?.get("reason") || "";
  const isUpgrade = reason === "extra";

  useEffect(() => {
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Only fetch owned property count when needed to filter plans
    if (!isUpgrade || !user) return;
    api.get("/properties").then((r) => setOwnedCount(r.data.length)).catch(() => setOwnedCount(1));
  }, [isUpgrade, user]);

  const startCheckout = async (packageId) => {
    setLoading((l) => ({ ...l, [packageId]: true }));
    try {
      const { data } = await api.post("/payments/checkout", {
        origin_url: window.location.origin,
        package_id: packageId,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(t("payment_failed"));
        setLoading((l) => ({ ...l, [packageId]: false }));
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading((l) => ({ ...l, [packageId]: false }));
    }
  };

  const allPackages = config?.packages ? Object.entries(config.packages) : [];
  // Filter: when upgrading for more properties, hide plans that don't grant more capacity
  const packages = isUpgrade && ownedCount != null
    ? allPackages.filter(([, pkg]) => Number(pkg.max_properties) > ownedCount)
    : allPackages;

  const body = (
    <div className="max-w-4xl w-full">
      <div className="text-center mb-8">
        <div className="w-14 h-14 rounded-2xl bg-[#2A5948] text-white flex items-center justify-center mx-auto mb-4">
          {isUpgrade ? <Building2 size={26} /> : <Sparkles size={26} />}
        </div>
        <h2 className="text-3xl sm:text-4xl font-semibold">
          {isUpgrade ? t("upgrade_for_more_properties") : t("subscribe_title")}
        </h2>
        <p className="text-sm text-[#5C6053] mt-2 max-w-xl mx-auto">
          {isUpgrade ? t("upgrade_desc") : t("subscribe_desc")}
        </p>
      </div>

      {packages.length === 0 ? (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6 text-center text-sm text-[#5C6053]" data-testid="no-plans-available">
          {t("no_upgrade_available")}
        </div>
      ) : (
        <div className={`grid grid-cols-1 ${packages.length > 1 ? "md:grid-cols-2" : "md:grid-cols-1 md:max-w-md md:mx-auto"} gap-4`}>
          {packages.map(([pid, pkg]) => (
            <PlanCard
              key={pid}
              id={pid}
              pkg={pkg}
              lang={lang}
              t={t}
              highlighted={pid === "premium"}
              loading={!!loading[pid]}
              onClick={() => startCheckout(pid)}
            />
          ))}
        </div>
      )}

      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#5C6053]">
        <ShieldCheck size={12} /> {t("secured_by_ompay")}
      </div>
    </div>
  );

  if (embedded) return body;

  return (
    <div className="min-h-screen grain-bg">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
          <a href="/app" className="flex items-center gap-2 font-semibold text-[#2A5948]">
            <Home size={18} />
            <span>{t("brand")}</span>
          </a>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-10 flex items-center justify-center">
        {body}
      </div>
    </div>
  );
}

function PlanCard({ id, pkg, lang, t, highlighted, loading, onClick }) {
  const perks = id === "premium"
    ? [t("perk_3_properties"), t("perk_reports"), t("perk_calendar"), t("perk_support")]
    : [t("perk_1_property"), t("perk_reports"), t("perk_calendar"), t("perk_support")];

  return (
    <div
      data-testid={`plan-card-${id}`}
      className={`relative bg-white rounded-2xl p-6 sm:p-8 shadow-sm border-2 transition-all ${
        highlighted ? "border-[#2A5948] scale-[1.02]" : "border-[#E5E1D8]"
      }`}
    >
      {highlighted && (
        <div className="absolute -top-3 inset-x-0 flex justify-center">
          <span className="text-[10px] px-2.5 py-1 rounded-full bg-[#C96A52] text-white font-medium tracking-wide" data-testid="premium-badge">
            {t("recommended")}
          </span>
        </div>
      )}
      <h3 className="text-xl font-semibold">
        {lang === "ar" ? pkg.label_ar : pkg.label_en}
      </h3>
      <div className="mt-4 flex items-baseline gap-2">
        <span className={`text-4xl font-semibold ${highlighted ? "text-[#2A5948]" : "text-[#1C1E17]"}`} data-testid={`plan-price-${id}`}>
          {pkg.amount}
        </span>
        <span className="text-sm text-[#5C6053]">{t("omr")} / {t("month")}</span>
      </div>
      <div className="text-xs text-[#5C6053] mt-1">
        {t("max_properties")}: <span className="font-semibold">{pkg.max_properties}</span>
      </div>

      <ul className="mt-5 space-y-2 text-sm">
        {perks.map((p, i) => (
          <li key={i} className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-[#2A5948] flex-shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`select-plan-${id}`}
        onClick={onClick}
        disabled={loading}
        className={`mt-6 w-full py-3 rounded-xl font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60 ${
          highlighted
            ? "brand-btn"
            : "bg-white text-[#2A5948] border-2 border-[#2A5948] hover:bg-[#F3EFE7]"
        }`}
      >
        <CreditCard size={16} />
        {loading ? "..." : t("pay_and_activate")}
      </button>
    </div>
  );
}
